import { loadHtmlPartialScripts } from '../../../utils/loadHtmlPartialScripts'
import { focusTrap } from '../../../utils/focusTrap'
import { GMXTabs } from '../tabs/tabs'

/**
 * Panel
 */
export class GMXPanel extends HTMLElement {
	private static readonly BASE_Z_INDEX = 1000
	private static openPanels = new Set<GMXPanel>()
	private static nextZIndex = GMXPanel.BASE_Z_INDEX

	panelContainerEl: HTMLElement
	panelHeaderEl: HTMLElement
	galleryHeaderTabs: GMXTabs
	galleryTabEls: NodeListOf<HTMLElement>
	ctaContainerEl: HTMLElement
	closeBtn: HTMLButtonElement
	focusTrap: (e: KeyboardEvent) => void

	private resizeObserver: ResizeObserver

	/* Height of the header (if exists) */
	private headerHeight: number

	static observedAttributes = ['data-show']

	private isClosing = false
	closeLocked = false

	private boundComponentClickHandler = this.handleComponentClick.bind(this)
	private boundCloseBtnClick = this.handleCloseBtnClick.bind(this)

	private galleryTabsIntersectionObserver: IntersectionObserver

	get defaultGalleryTab(): string {
		return this.getAttribute('data-default-gallery-tab')
	}
	set defaultGalleryTab(val: string) {
		this.setAttribute('data-default-gallery-tab', val)
	}

	get panelData(): Record<string, string> {
		try {
			return this.getAttribute('data-panel-data') ? JSON.parse(this.getAttribute('data-panel-data')) : {}
		} catch (error) {
			window.console.error(`ERROR:  Failed to parse panel data: ${error}`)
			return {}
		}
	}
	set panelData(data: Record<string, string>) {
		if (!data) {
			this.removeAttribute('data-panel-data')
		} else if (typeof data === 'object') {
			this.setAttribute('data-panel-data', JSON.stringify(data))
		}
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (this.isConnected) {
			if (name === 'data-show' && oldValue !== newValue && this.hasAttribute('data-panel-loaded')) {
				if (newValue !== null) {
					this.showPanel()
				} else {
					this.hidePanel()
				}
			}
		}
	}

	get panelType(): string {
		return this.getAttribute('data-panel-type')
	}
	set panelType(val: string) {
		this.setAttribute('data-panel-type', val)
	}

	async connectedCallback(): Promise<void> {
		this.panelContainerEl = this.querySelector('.panel-container')

		// make the panel inert by default
		this.setAttribute('inert', 'true')

		if (!this.panelContainerEl && this.id) {
			// Custom element is empty, go fetch content
			await this.fetchAndInsertPanelPartial()
		} else if (this.hasAttribute('data-show')) {
			this.showPanel()
		}

		this.panelHeaderEl = this.querySelector('.panel-header')
		this.ctaContainerEl = this.querySelector('.cta-container')
		this.closeBtn = this.querySelector('button.close-btn') as HTMLButtonElement

		if (this.panelType === 'gallery-panel') {
			this.galleryHeaderTabs = this.panelHeaderEl.querySelector('gmx-tabs')
			this.galleryTabEls = this.panelContainerEl.querySelectorAll('.gmx-tab-content-pane')

			this._setGalleryTabScrollOffset()
			this._setGalleryTabObservers()

			this.resizeObserver = new ResizeObserver(entries => {
				if (entries.length && Math.round(entries[0].borderBoxSize[0].blockSize) !== this.headerHeight) {
					this._setGalleryTabScrollOffset()
				}
			})

			this.resizeObserver.observe(this.panelHeaderEl)
		}

		if (this.panelType !== 'inset') {
			this.addEventListener('click', this.boundComponentClickHandler)
		} else {
			this.closeBtn?.addEventListener('click', this.boundCloseBtnClick)
		}

		this.focusTrap = focusTrap(this.panelContainerEl, this.querySelector('gmx-form') ? '.step.active' : '')
		this.addEventListener('keydown', this.focusTrap)
	}

	disconnectedCallback(): void {
		const wasOpen = GMXPanel.openPanels.has(this)
		GMXPanel.unregisterOpenPanel(this)

		if (wasOpen && !GMXPanel.hasOpenPanels()) {
			GMXPanel.restoreBodyScroll()
		}

		if (this.resizeObserver) {
			this.resizeObserver.unobserve(this.panelHeaderEl)
		}

		if (this.galleryTabsIntersectionObserver) {
			this.galleryTabsIntersectionObserver.disconnect()
		}

		if (this.panelType !== 'inset') {
			this.removeEventListener('click', this.boundComponentClickHandler)
		} else {
			this.closeBtn?.removeEventListener('click', this.boundCloseBtnClick)
		}

		this.removeEventListener('keydown', this.focusTrap)
	}

	private static registerOpenPanel(panel: GMXPanel): void {
		if (!panel.isConnected) {
			return
		}

		GMXPanel.nextZIndex += 1
		panel.style.zIndex = String(GMXPanel.nextZIndex)
		GMXPanel.openPanels.delete(panel)
		GMXPanel.openPanels.add(panel)
	}

	private static unregisterOpenPanel(panel: GMXPanel): void {
		GMXPanel.openPanels.delete(panel)

		if (GMXPanel.openPanels.size === 0) {
			GMXPanel.nextZIndex = GMXPanel.BASE_Z_INDEX
		}
	}

	private static hasOpenPanels(): boolean {
		return GMXPanel.openPanels.size > 0
	}

	private static restoreBodyScroll(): void {
		document.body.classList.remove('scroll-disabled')
		document.body.style.height = ''
		document.body.style.overflow = ''
	}

	/**
	 * Callback to handle click event of component.
	 * @param {Event} e
	 */
	handleComponentClick(e: Event): void {
		const target = e.target as HTMLElement

		if (!target.classList.contains('panel-container') && !target.closest('.panel-container')) {
			this.hidePanel()
		} else if (target.classList.contains('close-btn')) {
			this.handleCloseBtnClick()
		} else if (this.panelType === 'form-panel' && target.tagName.toLowerCase() === 'gmx-form') {
			this.hidePanel()
		}
	}

	/**
	 * Go out and fetch the panel partial content and insert as a child of the component
	 */
	async fetchAndInsertPanelPartial(): Promise<void> {
		try {
			const response = await fetch(this.id)
			if (!response.ok) {
				throw new Error()
			}

			const html = await response.text()
			this.innerHTML = html

			this.panelContainerEl = this.querySelector('.panel-container')

			loadHtmlPartialScripts(this)

			this.setAttribute('data-panel-loaded', '')

			if (this.hasAttribute('data-show')) {
				this.showPanel()
			}
		} catch (error) {
			window.console.error(`ERROR:  Failed to load panel partial: ${error}`)
		}
	}

	/**
	 * Method to handle showing panel
	 */
	showPanel(): void {
		if (this.hasAttribute('data-panel-data')) {
			this._processPanelData()
		}

		this._applyVehicleSelectorDefaults()

		const alreadyOpen = this.classList.contains('panel-open')
		GMXPanel.registerOpenPanel(this)

		if (window.browserDetectSingleton) {
			window.browserDetectSingleton.setScrollbarWidthOffset() /* Ensure layout doesn't shift */
		}

		/* Must add the class AND inline styles for apps that inject the nav and don't have the global CSS. */
		document.body.classList.add('scroll-disabled')
		document.body.style.height = '100%'
		document.body.style.overflow = 'hidden'

		this._setActiveTabForGallery()

		this.setAttribute('data-show', '')
		this.removeAttribute('inert')
		this.classList.add('panel-open')

		if (alreadyOpen) {
			return
		}

		this.panelContainerEl.addEventListener(
			'transitionend',
			() => {
				if (this.dataset.show !== undefined) {
					const formEl = this.querySelector('gmx-form')

					if (formEl) {
						const closeBtnActiveFormStep = formEl.querySelector(
							'.step.active button.close-btn'
						) as HTMLButtonElement

						// @ts-expect-error:  TypeScript is complaining about focusVisible, but it's a valid option for focus()
						closeBtnActiveFormStep?.focus({ focusVisible: true })
					} else {
						const closeBtn = this.querySelector('button.close-btn') as HTMLButtonElement

						// @ts-expect-error:  TypeScript is complaining about focusVisible, but it's a valid option for focus()
						closeBtn?.focus({ focusVisible: true })
					}
				}
				this.classList.add('panel-open')
			},
			{ once: true }
		)

		/* To create the Scrim experience */
		if (this.panelType !== 'inset') {
			if (this.panelType === 'full' || this.panelType === 'form-panel') {
				this.style.backdropFilter = 'var(--blur-none)'
			} else {
				this.style.backdropFilter = 'var(--blur-default)'
			}

			this.style.backgroundColor = 'var(--colors-fill-dark-40)'
		}
	}

	/**
	 * Method to handle hiding the panel.
	 */
	hidePanel(): void {
		if (this.closeLocked) {
			if (!this.hasAttribute('data-show')) {
				this.setAttribute('data-show', '')
			}
			return
		}

		if (this.isClosing) {
			return
		} else {
			this.isClosing = true
		}

		this.panelContainerEl.addEventListener(
			'transitionend',
			() => {
				this.dispatchEvent(new CustomEvent('close'))
				this.isClosing = false

				if (this.hasAttribute('data-show')) {
					return
				}

				GMXPanel.unregisterOpenPanel(this)
				this.classList.remove('panel-open')
				this.style.zIndex = ''

				if (!GMXPanel.hasOpenPanels()) {
					GMXPanel.restoreBodyScroll()
				}
			},
			{ once: true }
		)

		this.removeAttribute('data-show')
		this.setAttribute('inert', 'true')

		/* For the Scrim */
		this.style.backdropFilter = ''
		this.style.backgroundColor = ''
	}

	/**
	 * For the gallery pane use case, since anchors can be scrolled to and the header is sticky, need to offset
	 * the scroll by the height of the header so you are anchored to the right place
	 * @private
	 */
	private _setGalleryTabScrollOffset(): void {
		this.headerHeight = Math.round(this.panelHeaderEl.offsetHeight)

		this.galleryTabEls.forEach((tabEl: HTMLElement) => {
			tabEl.style.scrollMarginTop = `${this.headerHeight}px`
		})
	}

	/**
	 * Setup scroll observers on each of the Gallery Tab Panes and 'watch' for intersections to be able to determine
	 * which tab is active based on the scroll position (ie, 50% of the tab pane is in view).
	 * @private
	 */
	private _setGalleryTabObservers(): void {
		this.galleryTabsIntersectionObserver = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						this.galleryHeaderTabs.setActiveTab(entry.target.id, true)
					}
				})
			},
			{
				root: null,
				rootMargin: '-50% 0%',
				threshold: 0,
			}
		)

		this.galleryTabEls?.forEach((tabEl: HTMLElement) => {
			this.galleryTabsIntersectionObserver.observe(tabEl)
		})
	}

	private _setActiveTabForGallery(): void {
		const defaultActiveTab = this.defaultGalleryTab

		if (defaultActiveTab) {
			this.galleryHeaderTabs?.setActiveTab(defaultActiveTab, true)

			const panelRect = this.querySelector('.panel-content-body').getBoundingClientRect(),
				tabTargetRect = this.querySelector(`.gmx-tab-content-pane#${defaultActiveTab}`)?.getBoundingClientRect()

			if (tabTargetRect) {
				const offset = tabTargetRect.top - panelRect.top

				this.scrollTop = offset
			}
		}
	}

	/**
	 * Some panels, mostly forms, have default data to prepopulate the form with.  This method looks for that data and if exists, applies it to the correct form elements within the panel.
	 * @private
	 */
	private _processPanelData(): void {
		if (this.panelData) {
			if (this.panelType === 'form-panel') {
				const formEl = this.querySelector('gmx-form')

				if (formEl) {
					const formVehicleSelectorEl = formEl.querySelector('gmx-vehicle-selector-form-element')
					const formDealerSelector = formEl.querySelector('gmx-dealer-selector')

					if (formVehicleSelectorEl) {
						if (this.panelData['vehicleSelectorYear']) {
							formVehicleSelectorEl.setAttribute(
								'data-default-year',
								this.panelData['vehicleSelectorYear']
							)
						}

						if (this.panelData['vehicleSelectorModel']) {
							formVehicleSelectorEl.setAttribute(
								'data-default-model',
								this.panelData['vehicleSelectorModel']
							)
						}
					}

					if (formDealerSelector) {
						if (this.panelData['dealerBacId']) {
							formDealerSelector.setAttribute(
								'data-preselected-dealer-bac',
								this.panelData['dealerBacId']
							)
						}
					}
				}
			}
		}
	}

	/**
	 * Re-apply vehicle selector year/model defaults after the form was reset on close.
	 * @private
	 */
	private _applyVehicleSelectorDefaults(): void {
		if (this.panelType !== 'form-panel') {
			return
		}

		const vehicleSelectorEl = this.querySelector('gmx-vehicle-selector-form-element') as {
			applyDefaults?: () => void
		} | null
		vehicleSelectorEl?.applyDefaults?.()
	}

	handleCloseBtnClick(): void {
		this.hidePanel()
	}
}

if (!customElements.get('gmx-panel')) {
	customElements.define('gmx-panel', GMXPanel)
}
